import { get } from '../api/api'
import { showMessage } from './snackbar'
import { ROUTES } from '../routes'
import i18n from '../i18n'

export const isAxiosNetworkError = (error: unknown): boolean => {
    const e = error as Record<string, unknown>
    return !!e && e['isAxiosError'] === true && e['message'] === 'Network Error'
}

export const isAxiosServerError = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') return false
    const e = error as Record<string, unknown>
    const status = (e['response'] as Record<string, unknown> | undefined)?.['status']
    return e['isAxiosError'] === true && typeof status === 'number' && status >= 500
}

export const isAxiosRateLimited = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') return false
    const e = error as Record<string, unknown>
    const status = (e['response'] as Record<string, unknown> | undefined)?.['status']
    return e['isAxiosError'] === true && status === 429
}

export const sanitizeAxiosError = (error: unknown): unknown => {
    if (error && typeof error === 'object') {
        const e = error as Record<string, unknown>
        if (e['isAxiosError'] && e['config'] && typeof e['config'] === 'object') {
            const config = e['config'] as Record<string, unknown>
            if (config['data']) {
                config['data'] = '[REDACTED]'
            }
            // Manually-attached auth headers (e.g. signupSession.ts's SSO bearer token) never
            // go through api.ts's interceptor, so nothing else strips them before this error
            // reaches console.warn/Sentry.
            if (config['headers'] && typeof config['headers'] === 'object') {
                const headers = config['headers'] as Record<string, unknown>
                for (const key of Object.keys(headers)) {
                    if (key.toLowerCase() === 'authorization') headers[key] = '[REDACTED]'
                }
            }
        }
    }
    return error
}

const isAdapterUnavailable = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') return false
    const e = error as Record<string, unknown>
    const status = (e['response'] as Record<string, unknown> | undefined)?.['status']
    return (
        (e['isAxiosError'] === true && (status === 503 || e['code'] === 'ECONNABORTED')) || isAxiosNetworkError(error)
    )
}

type AdapterErrorOptions = {
    navigate?: (path: string) => void
    /**
     * Whether the caller still wants an outcome. Every message and redirect below is checked
     * against it, so a caller that has gone away — an unmounted page, a superseded request — is
     * never spoken for.
     *
     * The 401 branch asks again after its own probe: that probe is a retrying request with a
     * timeout of its own (MD-1503 gave every request a 45s ceiling, and `get` retries once), so
     * it can answer well over a minute after the call that failed, long enough for the caller to
     * have gone in the meantime. Callers that outlive their requests can omit it.
     */
    isCurrent?: () => boolean
}

// Handles known adapter error cases (503, 401, 404) with user-facing feedback.
// Re-throws for unexpected errors so callers can handle or log them.
export const handleAdapterError = async (
    error: unknown,
    adapter: string,
    options?: AdapterErrorOptions
): Promise<null> => {
    // Read at each use rather than captured once: the whole point is to notice the caller going
    // away *during* this call, which for the 401 branch happens while its probe is in flight.
    const stillWanted = () => options?.isCurrent?.() !== false

    if (isAdapterUnavailable(error)) {
        // A timeout lands here too (`ECONNABORTED`), and since MD-1503 that is an ordinary way
        // for a slow adapter call to end rather than a rarity, so this branch is as able to
        // arrive late as the probing one below.
        if (stillWanted()) showMessage(i18n.t('adapter.connection_error', { adapter }), 'error')
        return null
    }

    const status = (error as Record<string, unknown> | null)?.['response'] as Record<string, unknown> | undefined
    const statusCode = status?.['status']

    if (statusCode === 401) {
        try {
            const initResponse = await get<{ auth_uri?: string }>(`/adapters/${adapter}/init`)
            if (!stillWanted()) return null

            if (initResponse?.auth_uri) {
                showMessage(i18n.t('adapter.reconnect_required', { adapter }), 'warning')
                options?.navigate?.(ROUTES.integrations.list.path)
            } else {
                // The connection probes as usable, yet the call that got here was still refused.
                // Announcing a reconnection used to read as though that call had gone through, so
                // a report that never reached the patient file looked like one that had (MD-1574).
                //
                // Marking the adapter connected off this probe went with that announcement, and
                // goes with it: a refused call is not the moment to upgrade what the rest of the
                // app believes about the integration, and `useAdapterInit` and `IntegrationsList`
                // both settle the flag from `/connected`, which answers about the connection
                // rather than about consent.
                showMessage(i18n.t('adapter.request_refused', { adapter }), 'warning')
            }
        } catch {
            if (!stillWanted()) return null

            showMessage(i18n.t('adapter.reconnect_failed', { adapter }), 'error')
            options?.navigate?.(ROUTES.integrations.list.path)
        }
        return null
    }

    if (statusCode === 404) {
        return null
    }

    throw error
}
